import { useUsersList } from '../../../api/healthCoach/useUsersList';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useCareTeamMember from './useCareTeamMember';
import { useDashboardCoach } from './useDashboardCoach';
import {
  usePrototypeState,
  type RosterState,
} from '../../PrototypeBar/PrototypeStateContext';
import {
  DEFAULT_EXAMPLE_PROVIDER,
  DEFAULT_USER_TYPES,
  EnrollmentStatus,
  isPastTiming,
  VisitConfirmation,
  VisitTiming,
} from '../constants';
import { CareCoordinationStatus, RosterPatient } from '../../../models/user';
import {
  displayedAppointment,
  isRecentSignup,
  isVisitWithCareTeam,
  matchesAttributes,
  matchesCareCoordination,
  matchesCareTeam,
  matchesEnrollmentStatus,
  matchesMemberStatus,
  matchesSearch,
  matchesVisitStatus,
} from '../filters';

/**
 * Filter state for the CareOS roster.
 *
 * Two layers, not one:
 *  - **Applied** filters live in the URL, same as before — the sole input to
 *    `filteredUsers`/`stats`/`activeFilterCount`/`isPanelEmpty`, and what
 *    makes a view bookmarkable/shareable.
 *  - **Draft** filters are local state every sidebar control actually reads
 *    from and writes to. They mirror applied state until a user changes
 *    something, and only reach the URL (and the table) when `applyFilters` is
 *    called — the "Apply Filters" button. This batches what would otherwise
 *    be one server round-trip per click into one request per Apply, per team
 *    feedback (Aug 2026 round 3).
 *
 * Search is the one exception: it's excluded from the draft/Apply gating on
 * purpose (team decision) and filters live, same as it always has.
 *
 * List values are JSON-encoded arrays in the URL, matching the encoding the
 * roster already used for its multi-selects.
 *
 * Filtering is entirely client-side. It used to be split — coach and provider
 * went to the server as `coach_id` / `care_team_member_id` while everything
 * else filtered locally — but those params take a single value each and the
 * brief calls for multi-select per role. Handing the whole panel to the client
 * keeps every dimension consistent and composable. At real panel sizes the
 * endpoint will need array params to do this server-side; noted for handoff.
 */
const PARAM = {
  search: 'search',
  coaches: 'coaches',
  providers: 'providers',
  careModels: 'careModels',
  risks: 'risks',
  visitTiming: 'visitTiming',
  visitConfirmations: 'visitConfirmations',
  careCoordination: 'careCoordination',
  enrollment: 'enrollment',
  userTypes: 'types',
  conditions: 'conditions',
} as const;

type ParamKey = (typeof PARAM)[keyof typeof PARAM];

/**
 * Session-scoped snapshot of the roster's *applied* filter query string.
 *
 * Rows navigate to the Patient Profile via a fresh path
 * (`/provider-home/patient/:id?from=hc-dashboard`, see
 * `HealthCoachDashboardListItem`), and both its "back to Care" affordance
 * and the SideNav's Care item navigate back to a bare `/hc-dashboard` —
 * neither carries the roster's query string with it. That unmounts and
 * remounts this hook with an empty URL, so whatever richer filter selection
 * was applied before the click would otherwise be silently lost and replaced
 * by the True User + example-provider default (see the seeding effect
 * below). Stashing the last-applied query string here lets that effect
 * restore it instead, once per return trip.
 *
 * sessionStorage rather than localStorage: scoped to the tab, and gone once
 * the session ends rather than resurfacing on a later, unrelated visit —
 * same rationale as `PrototypeStateContext`'s persistence.
 */
const FILTER_SESSION_STORAGE_KEY = 'ayble.hcDashboard.filters.v1';

/**
 * The list-valued filters. They share one keyed setter and one generic
 * encode/decode path regardless of how many values each actually admits.
 *
 * The single-select filters — visit timing, care coordination, enrollment
 * status — deliberately live *outside* this record as scalars, so the type
 * says what the control does instead of leaving a one-element array to be
 * policed by the UI. The cost is that each needs threading through the
 * draft/applied plumbing by hand; see `applyFilters` and the resync effect.
 *
 * Several fields here render single-select despite staying array-shaped:
 * `visitConfirmations` always has (it's a two-value boolean, so the only
 * multi-selection possible — both pills — is already equivalent to selecting
 * neither, and the predicate treats it that way); `coaches`, `providers`,
 * `careModels`, and `userTypes` joined it in the Aug 2026 filter-logic pass
 * (`FilterSelect`'s `isMulti={false}`) because a member only ever has one of
 * each. `risks` and `conditions` are the only genuinely multi-select fields
 * left. None of that cardinality is enforced here — the array shape stays so
 * this record doesn't need a scalar carve-out per field, same as
 * `visitConfirmations` always did; the control caps what a user *can* select,
 * the predicate just checks array membership either way.
 */
type SelectedFilters = {
  coaches: string[];
  providers: string[];
  careModels: string[];
  risks: string[];
  visitConfirmations: VisitConfirmation[];
  userTypes: string[];
  conditions: string[];
};

const readList = (params: URLSearchParams, key: ParamKey): string[] => {
  const raw = params.get(key);
  if (!raw) return [];

  try {
    // `params.get` has already percent-decoded once. The extra decode is for
    // links written by the roster's previous encoding, which added a manual
    // pass on top of URLSearchParams' own — harmless on a plain JSON string.
    const parsed = JSON.parse(
      raw.trimStart().startsWith('[') ? raw : decodeURIComponent(raw)
    );
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    // A hand-edited or stale URL should not take the screen down.
    return [];
  }
};

// No encodeURIComponent here: URLSearchParams encodes on write, and doing it
// twice produces %255B-style URLs that are unreadable in a shared link.
const encodeList = (values: string[]) => JSON.stringify(values);

/** Order-insensitive equality for the string-array filter values. */
const sameValues = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

/**
 * Prototype-bar scenarios that are really a filter selection, expressed as the
 * selection itself.
 *
 * Switching to one of these applies the filters a care team member would have
 * clicked, so the sidebar shows what's driving the list. It writes straight to
 * *applied* state (bypassing the draft/Apply gating below) — a demo scenario
 * is meant to show its result immediately, not wait for a click — and the
 * draft-resync effect then pulls the sidebar's pending edits back in line
 * with it, discarding whatever was mid-edit. That's intentional: switching
 * scenarios is a deliberate context jump, and half-finished edits from the
 * previous context aren't likely to still make sense in the new one.
 *
 * `null` means the scenario clears these filters instead of setting them.
 */
const SCENARIO_FILTERS: Record<
  RosterState,
  {
    careCoordination: CareCoordinationStatus;
    visitTiming?: VisitTiming;
  } | null
> = {
  mixed: null,
  // The outreach worklist: never logged in, and nothing on the books.
  //
  // Care coordination used to select `never_logged_in` *or* `onboarding`;
  // round 6's single-select can't express an OR, so it narrows to the sharper
  // of the two. Never-logged-in is the unambiguous signal — someone stuck
  // mid-onboarding has at least engaged once — and it's the list a care team
  // would work first.
  //
  // `none_scheduled` is here to complete the definition rather than to narrow
  // it. Every never-logged-in fixture already has nothing booked, so this
  // changes no results today; what it changes is that the sidebar now *states*
  // the whole worklist instead of leaving "and none of them have an
  // appointment" as something you have to notice in the table. That's this
  // map's job — see the doc comment above: a scenario is the selection a care
  // team member would have clicked, and someone building an outreach list
  // would have clicked both.
  //
  // Worth knowing it stops being a no-op the moment real data contains a
  // never-logged-in member with a visit booked (staff can schedule someone who
  // has never opened the app). Those members would drop out of this scenario.
  // That's the intended reading — a booked visit is the outreach — but it is a
  // definition, not a tautology, so revisit it here rather than assuming.
  //
  // Still deliberately independent of signup date: someone who signed up 3
  // weeks ago and never logged in belongs on this list. Enrollment Status is
  // where signup recency lives now, and this scenario leaves it alone.
  'needs-outreach': {
    careCoordination: 'never_logged_in',
    visitTiming: 'none_scheduled',
  },
  // Not a selection — an empty panel. Handled where the data is resolved.
  empty: null,
};

export const useHealthCoachDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Keep the last-applied filter state around for the seeding effect below
  // to restore on a return trip from the patient profile. Skipped when the
  // URL is empty — the instant after a bare `/hc-dashboard` navigation,
  // before the seeding effect has restored or re-defaulted it — so that
  // moment doesn't overwrite a richer selection with nothing.
  useEffect(() => {
    const qs = searchParams.toString();
    if (!qs) return;
    try {
      sessionStorage.setItem(FILTER_SESSION_STORAGE_KEY, qs);
    } catch {
      // Private-mode storage failures shouldn't break filtering.
    }
  }, [searchParams]);

  const { initialSelectedProviderName, providerOptions } = useCareTeamMember();
  const { coachOptions, initialSelectedCoachId } = useDashboardCoach();

  // The whole panel: no server-side narrowing, see the note above.
  const { usersList: allUsers, isLoading: isLoadingUsers } = useUsersList({});
  const { rosterState } = usePrototypeState();

  // 'empty' is the only scenario that withholds data: it stands for a panel
  // with nobody on it, which is a different thing from filters excluding
  // everybody and is why the list distinguishes the two empty states.
  const users = useMemo(
    () => (rosterState === 'empty' ? [] : allUsers),
    [allUsers, rosterState]
  );

  // Search stays live and outside the draft/Apply gating — team decision.
  const search = searchParams.get(PARAM.search) ?? '';

  // --- Applied state: the URL, unchanged mechanism from before ---------
  const appliedVisitTiming =
    (searchParams.get(PARAM.visitTiming) as VisitTiming) || null;
  // Cast rather than validated, same trust level as `visitTiming` above: these
  // only ever get written from their own option lists, and an unrecognized
  // value off a hand-edited URL simply matches no member rather than throwing.
  const appliedCareCoordination =
    (searchParams.get(PARAM.careCoordination) as CareCoordinationStatus) || null;
  const appliedEnrollment =
    (searchParams.get(PARAM.enrollment) as EnrollmentStatus) || null;

  const appliedSelected: SelectedFilters = useMemo(
    () => ({
      coaches: readList(searchParams, PARAM.coaches),
      providers: readList(searchParams, PARAM.providers),
      careModels: readList(searchParams, PARAM.careModels),
      risks: readList(searchParams, PARAM.risks),
      visitConfirmations: readList(
        searchParams,
        PARAM.visitConfirmations
      ) as VisitConfirmation[],
      userTypes: readList(searchParams, PARAM.userTypes),
      conditions: readList(searchParams, PARAM.conditions),
    }),
    [searchParams]
  );

  // --- Draft state: what the sidebar controls actually read/write ------
  // Lazily seeded from whatever's already in the URL on first render (a
  // shared/bookmarked link opens with both layers already in agreement, no
  // Apply click needed), then kept in sync with applied state by the effect
  // below whenever applied state changes for a reason *other* than Apply
  // itself — the initial default seed, a Clear All, or a prototype-bar
  // scenario switch. Apply is the only path that goes the other direction
  // (draft → applied).
  const [draftVisitTiming, setDraftVisitTiming] = useState<VisitTiming | null>(
    appliedVisitTiming
  );
  const [draftCareCoordination, setDraftCareCoordination] =
    useState<CareCoordinationStatus | null>(appliedCareCoordination);
  const [draftEnrollment, setDraftEnrollment] = useState<EnrollmentStatus | null>(
    appliedEnrollment
  );
  const [draftSelected, setDraftSelected] = useState(appliedSelected);

  // `appliedSelected` is recomputed (a new object) on *every* searchParams
  // change, including a live `search` keystroke — search shares the same
  // URLSearchParams as everything else, even though it's excluded from the
  // draft/applied split entirely. Resyncing on every object-identity change
  // rather than every actual value change meant typing in the search box
  // silently wiped out whatever pending, unapplied edits were sitting in
  // draft. This ref tracks the last-seen applied *values* so the resync below
  // only fires when something Apply/Clear All/seed/a scenario switch would
  // actually change, not on every URL write regardless of cause.
  const lastAppliedRef = useRef({
    selected: appliedSelected,
    visitTiming: appliedVisitTiming,
    careCoordination: appliedCareCoordination,
    enrollment: appliedEnrollment,
  });
  useEffect(() => {
    const last = lastAppliedRef.current;
    const selectedChanged = (Object.keys(appliedSelected) as (keyof SelectedFilters)[]).some(
      (key) => !sameValues(last.selected[key], appliedSelected[key])
    );
    const timingChanged = last.visitTiming !== appliedVisitTiming;
    const careCoordinationChanged =
      last.careCoordination !== appliedCareCoordination;
    const enrollmentChanged = last.enrollment !== appliedEnrollment;
    lastAppliedRef.current = {
      selected: appliedSelected,
      visitTiming: appliedVisitTiming,
      careCoordination: appliedCareCoordination,
      enrollment: appliedEnrollment,
    };

    if (selectedChanged) setDraftSelected(appliedSelected);
    if (timingChanged) setDraftVisitTiming(appliedVisitTiming);
    if (careCoordinationChanged) setDraftCareCoordination(appliedCareCoordination);
    if (enrollmentChanged) setDraftEnrollment(appliedEnrollment);
  }, [
    appliedVisitTiming,
    appliedCareCoordination,
    appliedEnrollment,
    appliedSelected,
  ]);

  const isDirty = useMemo(
    () =>
      draftVisitTiming !== appliedVisitTiming ||
      draftCareCoordination !== appliedCareCoordination ||
      draftEnrollment !== appliedEnrollment ||
      (Object.keys(draftSelected) as (keyof SelectedFilters)[]).some(
        (key) => !sameValues(draftSelected[key], appliedSelected[key])
      ),
    [
      draftSelected,
      draftVisitTiming,
      draftCareCoordination,
      draftEnrollment,
      appliedSelected,
      appliedVisitTiming,
      appliedCareCoordination,
      appliedEnrollment,
    ]
  );

  const setParam = useCallback(
    (key: ParamKey, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /**
   * Default selection for a fresh or reset roster: True User + one example
   * provider (see `DEFAULT_EXAMPLE_PROVIDER`). Falls back to nothing if that
   * example isn't in the current provider list — a demo constant that stops
   * matching after a fixture change should silently no-op, not seed a filter
   * that returns everyone or crash.
   */
  const defaultProviderName = useMemo(
    () =>
      initialSelectedProviderName ??
      (providerOptions.some((o) => o.value === DEFAULT_EXAMPLE_PROVIDER)
        ? DEFAULT_EXAMPLE_PROVIDER
        : undefined),
    [initialSelectedProviderName, providerOptions]
  );

  // Seed the roster with the signed-in user's own panel, once. Resolves to
  // nothing unless the authenticated user is themselves a coach or care-team
  // member, so coaches land on their patients — everyone else gets the
  // hardcoded example provider via `defaultProviderName` instead.
  //
  // An empty URL is ambiguous between "first-ever visit" and "just came back
  // from the patient profile (or clicked Care in the nav) with an applied
  // filter selection from earlier this session" — see
  // `FILTER_SESSION_STORAGE_KEY` above. A stashed selection wins; the
  // hardcoded default only fires when there's genuinely nothing to restore.
  // A non-empty URL (a fresh bookmark or shared link) always wins over both —
  // this effect only ever fills in *missing* params.
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (hasSeeded.current) return;
    if (!coachOptions?.length && !providerOptions.length) return;
    hasSeeded.current = true;

    if (!searchParams.toString()) {
      let restored: string | null = null;
      try {
        restored = sessionStorage.getItem(FILTER_SESSION_STORAGE_KEY);
      } catch {
        // Private-mode storage failures fall through to the default below.
      }
      if (restored) {
        setSearchParams(new URLSearchParams(restored), { replace: true });
        return;
      }
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!next.get(PARAM.userTypes)) {
          next.set(PARAM.userTypes, encodeList(DEFAULT_USER_TYPES));
        }
        if (initialSelectedCoachId && !next.get(PARAM.coaches)) {
          next.set(PARAM.coaches, encodeList([initialSelectedCoachId]));
        }
        if (defaultProviderName && !next.get(PARAM.providers)) {
          next.set(PARAM.providers, encodeList([defaultProviderName]));
        }
        return next;
      },
      { replace: true }
    );
  }, [
    coachOptions,
    providerOptions,
    initialSelectedCoachId,
    defaultProviderName,
    searchParams,
    setSearchParams,
  ]);

  // Apply a prototype-bar scenario as a real filter selection — see
  // SCENARIO_FILTERS' doc comment for why this writes straight to applied
  // state rather than going through draft/Apply.
  const appliedScenario = useRef<RosterState | null>(null);
  useEffect(() => {
    const previous = appliedScenario.current;
    if (previous === rosterState) return;
    appliedScenario.current = rosterState;

    const scenario = SCENARIO_FILTERS[rosterState];

    // On first render there is nothing to undo, and clearing here would wipe
    // the filters off a shared or bookmarked URL before it ever rendered.
    if (previous === null && !scenario) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (scenario) {
          next.set(PARAM.careCoordination, scenario.careCoordination);
          // Set-or-clear rather than set-if-present: switching between
          // scenarios has to undo the previous one's timing selection, not
          // leave it applied under a scenario that never asked for it.
          if (scenario.visitTiming) {
            next.set(PARAM.visitTiming, scenario.visitTiming);
          } else {
            next.delete(PARAM.visitTiming);
          }
        } else {
          next.delete(PARAM.careCoordination);
          next.delete(PARAM.visitTiming);
        }
        return next;
      },
      { replace: true }
    );
  }, [rosterState, setSearchParams]);

  const filteredUsers = useMemo(() => {
    if (!users) return [];

    const now = new Date();
    // One AND group per role, OR within each — round 6's framework asks for
    // "members who have both Trent and Stefania". Note this reverses the
    // flattened-union this used to build on purpose; see `CareTeamFilters` in
    // filters.ts for the full rationale and for why Providers and NPs share
    // one pool (they read the same patient field, so ANDing them would match
    // nobody) — NPs fold into `providers` at the picklist level as of round 8
    // (see `useCareTeamMember`), so there's no separate list to merge here.
    const careTeamFilters = {
      coachIds: appliedSelected.coaches,
      providerNames: appliedSelected.providers,
    };

    const matched = users.filter((user) => {
      if (!matchesSearch(user, search)) return false;
      if (!matchesCareTeam(user, careTeamFilters)) return false;
      if (
        !matchesMemberStatus(user, {
          careModels: appliedSelected.careModels,
          risks: appliedSelected.risks,
        })
      ) {
        return false;
      }
      if (
        !matchesVisitStatus(
          user,
          {
            timing: appliedVisitTiming,
            confirmations: appliedSelected.visitConfirmations,
          },
          now
        )
      ) {
        return false;
      }
      if (!matchesCareCoordination(user, appliedCareCoordination)) {
        return false;
      }
      if (!matchesEnrollmentStatus(user, appliedEnrollment, now)) {
        return false;
      }
      if (
        !matchesAttributes(user, {
          userTypes: appliedSelected.userTypes,
          conditions: appliedSelected.conditions,
        })
      ) {
        return false;
      }

      return true;
    });

    // Two tiers, then soonest-first inside each:
    //   1. visits with the selected care team — "my appointments"
    //   2. visits with anyone else on the member's care team
    // and members with no visit at all last.
    //
    // This is what replaced round 1's attendee scoping (see
    // `matchesVisitStatus`): a visit with someone else no longer removes a
    // member from the list, it just ranks below your own. With nothing
    // selected in the Care Team section every member lands in tier 2, which
    // collapses this back to plain soonest-first.
    //
    // Applied unconditionally rather than only for the forward-looking
    // buckets, because "my appointments first" is the roster's default
    // resting order now, not something a timing filter turns on. "None
    // Scheduled" is unaffected in practice — nobody in that result has a
    // date to sort by.
    // In a past window the question is "what happened most recently", so the
    // date comparison flips (team decision, round 7). Everywhere else it stays
    // soonest-first.
    //
    // Nothing extra is needed to keep stale appointments off the top of the
    // default view: past appointments aren't displayed at all unless a past
    // window is selected (see `displayedAppointment`), and a member whose only
    // appointment has already happened has no date to sort by here — they land
    // with the no-appointment group, which is exactly where a row reading
    // "None scheduled" belongs.
    const past = isPastTiming(appliedVisitTiming);
    const sortableDate = (user: RosterPatient) => {
      const date = displayedAppointment(user, appliedVisitTiming, now)?.date;
      return date ? date.getTime() : null;
    };

    matched.sort((a, b) => {
      const aMine = isVisitWithCareTeam(a, careTeamFilters);
      const bMine = isVisitWithCareTeam(b, careTeamFilters);
      if (aMine !== bMine) return aMine ? -1 : 1;

      const aDate = sortableDate(a);
      const bDate = sortableDate(b);
      if (aDate === null && bDate === null) return 0;
      // Members with nothing to show sort last in both directions — "no
      // appointment" is not the most recent thing that happened to them.
      if (aDate === null) return 1;
      if (bDate === null) return -1;
      return past ? bDate - aDate : aDate - bDate;
    });

    return matched;
  }, [
    users,
    search,
    appliedVisitTiming,
    appliedCareCoordination,
    appliedEnrollment,
    appliedSelected,
  ]);

  // Panel stats. Both count the *filtered* list so the header answers
  // "how big is what I'm looking at", not "how big is the database".
  const stats = useMemo(() => {
    const now = new Date();
    const newThisWeek = filteredUsers.filter((user) => isRecentSignup(user, now)).length;

    return { totalPanel: filteredUsers.length, newThisWeek };
  }, [filteredUsers]);

  // Drives whether "Clear All" shows at all — computed off *draft*, which is
  // what the sidebar controls visually present, so it tracks what a user
  // perceives as "things I've set" (including pending, unapplied edits).
  const activeFilterCount = useMemo(
    () =>
      (search ? 1 : 0) +
      (draftVisitTiming ? 1 : 0) +
      (draftCareCoordination ? 1 : 0) +
      (draftEnrollment ? 1 : 0) +
      Object.values(draftSelected).reduce((sum, list) => sum + (list.length ? 1 : 0), 0),
    [
      search,
      draftVisitTiming,
      draftCareCoordination,
      draftEnrollment,
      draftSelected,
    ]
  );

  // The search box is uncontrolled and debounced internally, so clearing the
  // URL alone would leave its text on screen. Bumping this remounts it.
  const [resetToken, setResetToken] = useState(0);

  /**
   * Commits draft → applied: writes every draft value into the URL (merged
   * on top of whatever's already there, so `search` — which never touches
   * draft — survives untouched) and lets the resync effect fold it back into
   * draft on the next render. This is the only path, besides a scenario
   * switch or Clear All, that changes what the table actually shows.
   */
  const applyFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        (Object.keys(draftSelected) as (keyof SelectedFilters)[]).forEach((key) => {
          const values = draftSelected[key];
          if (values.length) next.set(PARAM[key], encodeList(values));
          else next.delete(PARAM[key]);
        });
        if (draftVisitTiming) next.set(PARAM.visitTiming, draftVisitTiming);
        else next.delete(PARAM.visitTiming);
        if (draftCareCoordination)
          next.set(PARAM.careCoordination, draftCareCoordination);
        else next.delete(PARAM.careCoordination);
        if (draftEnrollment) next.set(PARAM.enrollment, draftEnrollment);
        else next.delete(PARAM.enrollment);
        return next;
      },
      { replace: true }
    );
  }, [
    draftSelected,
    draftVisitTiming,
    draftCareCoordination,
    draftEnrollment,
    setSearchParams,
  ]);

  /**
   * The default selection a fresh load or a Clear All lands on: True User plus
   * the example provider (and the signed-in coach's own panel, when there is
   * one). One definition feeding both the URL write and the draft reset below,
   * so the two layers can't disagree about what "default" means.
   */
  const defaultSelected: SelectedFilters = useMemo(
    () => ({
      coaches: initialSelectedCoachId ? [initialSelectedCoachId] : [],
      providers: defaultProviderName ? [defaultProviderName] : [],
      careModels: [],
      risks: [],
      visitConfirmations: [],
      userTypes: DEFAULT_USER_TYPES,
      conditions: [],
    }),
    [initialSelectedCoachId, defaultProviderName]
  );

  const clearAllFilters = useCallback(() => {
    // A true reset, not a blank slate: back to the same True User + example
    // provider default a fresh page load seeds (see the seeding effect above)
    // — discarding both whatever's currently applied *and* any unsaved draft
    // edits, per team feedback (Aug 2026 round 3). A brand-new, empty
    // URLSearchParams (rather than merging onto `prev`) is what clears
    // `search` too, matching "true reset" rather than leaving stale search
    // text with a fresh filter set.
    const defaults = new URLSearchParams();
    defaults.set(PARAM.userTypes, encodeList(defaultSelected.userTypes));
    if (defaultSelected.coaches.length) {
      defaults.set(PARAM.coaches, encodeList(defaultSelected.coaches));
    }
    if (defaultSelected.providers.length) {
      defaults.set(PARAM.providers, encodeList(defaultSelected.providers));
    }
    setSearchParams(defaults, { replace: true });

    // Reset draft explicitly rather than leaning on the resync effect to do
    // it. That effect only fires for values that changed in *applied* state,
    // so a draft-only edit whose applied counterpart was already empty — a
    // pill toggled but never applied — would survive the reset and sit there
    // looking selected against a freshly cleared table. That's the opposite of
    // what Clear All promises, and it applied to every scalar filter here
    // (visit timing included) before round 6 made it easy to hit.
    setDraftSelected(defaultSelected);
    setDraftVisitTiming(null);
    setDraftCareCoordination(null);
    setDraftEnrollment(null);
    setResetToken((n) => n + 1);
  }, [setSearchParams, defaultSelected]);

  return {
    isLoadingUsers,
    filteredUsers,
    /**
     * Nobody on the panel at all, as opposed to filters excluding everyone.
     * The two need different empty states — telling someone to clear a filter
     * they never set sends them hunting through the sidebar for nothing.
     */
    isPanelEmpty: (users?.length ?? 0) === 0,
    stats,
    activeFilterCount,
    clearAllFilters,
    resetToken,

    // Whether draft state has anything Applied doesn't yet — drives the
    // Apply Filters button's disabled state.
    isDirty,
    applyFilters,

    // Options
    coachOptions,
    providerOptions,

    // Current selections — draft, so every section control reads/writes the
    // pending, not-yet-applied state. `filteredUsers`/`stats` above read the
    // separate applied copies instead.
    search,
    visitTiming: draftVisitTiming,
    /**
     * The *applied* timing, for the table rather than the sidebar.
     *
     * Every other selection this hook returns is the draft copy, because the
     * sidebar controls are what read them. The table is the opposite case: it
     * has to agree with what is actually filtering it, so a pill clicked but
     * not yet applied must not change how a row renders. Named distinctly
     * rather than overloading `visitTiming` precisely so that can't happen by
     * accident.
     */
    appliedVisitTiming,
    careCoordination: draftCareCoordination,
    enrollment: draftEnrollment,
    selected: draftSelected,

    // Setters. Every list filter goes through one keyed setter rather than a
    // dozen near-identical callbacks — the key is checked against `selected`,
    // so a typo is a type error rather than a filter that silently does
    // nothing. Both write to draft only; nothing here touches the URL or the
    // table until `applyFilters` runs. Search keeps its own setter and stays
    // wired straight to the URL/table, live.
    setFilter: useCallback(
      (key: keyof SelectedFilters, values: string[]) =>
        setDraftSelected((prev) => ({ ...prev, [key]: values })),
      []
    ),
    onSearchChange: useCallback(
      (value: string) => setParam(PARAM.search, value || null),
      [setParam]
    ),
    onVisitTimingChange: useCallback(
      // Cast, not validated: v[0] always comes from VISIT_TIMING_OPTIONS'
      // own values (see AppointmentsSection), so it's already a VisitTiming —
      // same trust level as the `as VisitTiming` cast on the URL-parsed value
      // above.
      (v: string[]) => setDraftVisitTiming((v[0] as VisitTiming) ?? null),
      []
    ),
    // The two single-select pill groups. Both take the array `PillToggleGroup`
    // hands back in `multiple={false}` mode — always empty or one element —
    // and narrow it to the scalar the state actually holds. Same cast rationale
    // as `onVisitTimingChange`: the value can only have come from the group's
    // own option list.
    onCareCoordinationChange: useCallback(
      (v: string[]) =>
        setDraftCareCoordination((v[0] as CareCoordinationStatus) ?? null),
      []
    ),
    onEnrollmentChange: useCallback(
      (v: string[]) => setDraftEnrollment((v[0] as EnrollmentStatus) ?? null),
      []
    ),
  };
};
